import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { lowerCase, startCase } from 'lodash';

import { SingleSelectFormControl } from '@admin/components/fields/single_select_form_control';
import { client } from '@admin/libraries/apollo';
import { OptionsQuery, useLogisticsSkusForAccountLazyQuery } from '@admin/schema';

import { Field, StyledRow, RemoveCargoDetailsButton } from './field';

type PurchaseOrder = OptionsQuery['logisticsPurchaseOrders'][number];

export const FulfillmentExpectationFields: React.FC<{
  purchaseOrders: PurchaseOrder[];
  item: any;
  index: number;
  removeFulfillmentExpectation(index: number): void;
  initialPurchaseOrderID: string | undefined;
  initialSkuID: string | undefined;
  hasItems: boolean;
}> = ({
  purchaseOrders,
  item,
  index,
  removeFulfillmentExpectation,
  initialPurchaseOrderID,
  initialSkuID,
  hasItems,
}) => {
  const { errors, register, watch } = useFormContext<{
    fulfillmentExpectations?: Array<{
      skuID: string;
      quantity: number;
      purchaseOrderID: string;
    }>;
  }>();

  const accountID = watch(`accountID`) as string;
  const [purchaseOrderID, setPurchaseOrderID] = useState<string>(initialPurchaseOrderID || '');
  const [skuID, setSkuID] = useState<string>(initialSkuID || '');

  const [getSkus, { data: skuData, loading }] = useLogisticsSkusForAccountLazyQuery({
    client,
    variables: { accountID },
  });

  useEffect(() => {
    if (accountID) {
      getSkus();
    }
  }, [accountID]);

  if (!accountID) {
    return <span>You Must First Select An Account</span>;
  }

  if (accountID && (loading || !skuData)) {
    return <span>Loading...</span>;
  }

  const poOptions = purchaseOrders
    .filter((po) => po.account.id === accountID)
    .map((po) => ({ value: po.id, label: `${po.number} (${po.account.customer.name})` }));
  const skuOptions = skuData?.logisticsSkus.map((sku) => ({
    value: sku.id,
    label: `${sku.value} (${startCase(lowerCase(sku.countingUnit))})`,
  }));

  return (
    <StyledRow className="row">
      <input
        type="hidden"
        id={`fe_id_${index}_hidden`}
        defaultValue={item.fulfillmentExpectationID}
        name={`fulfillmentExpectations[${index}].fulfillmentExpectationID`}
        ref={register()}
        disabled={true}
      />
      <div className="col-md-3">
        <Field error={errors.fulfillmentExpectations} id={`po_id_${index}_hidden`} label="Purchase Order Number">
          <SingleSelectFormControl
            id={`po_id_${index}`}
            value={purchaseOrderID}
            onChange={(id) => {
              if (id) setPurchaseOrderID(id);
            }}
            options={poOptions}
            disabled={hasItems}
          />
          <input
            type="hidden"
            id={`po_id_${index}_hidden`}
            name={`fulfillmentExpectations[${index}].purchaseOrderID`}
            ref={register()}
            defaultValue={purchaseOrderID}
          />
        </Field>
      </div>
      <div className="col-md-3">
        <Field error={errors.fulfillmentExpectations} id={`sku_id_${index}`} label="SKU Number" required={true}>
          <SingleSelectFormControl
            id={`sku_id_${index}`}
            value={skuID}
            onChange={(id) => {
              if (id) setSkuID(id);
            }}
            options={skuOptions ?? []}
            disabled={!skuData || hasItems}
          />
          <input
            type="hidden"
            id={`sku_id_${index}_hidden`}
            name={`fulfillmentExpectations[${index}].skuID`}
            ref={register()}
            value={skuID}
          />
        </Field>
      </div>
      <div className="col-md-2">
        <Field error={errors.fulfillmentExpectations} id={`quantity_${index}`} label="Quantity" required={true}>
          <input
            id={`quantity_${index}`}
            type="number"
            className="form-control"
            defaultValue={item.quantity}
            name={`fulfillmentExpectations[${index}].quantity`}
            ref={register()}
            onWheel={(e) => e.currentTarget.blur()}
            disabled={!skuData}
          />
        </Field>
      </div>
      <div className="col-md-1">
        <RemoveCargoDetailsButton kind="danger" onClick={() => removeFulfillmentExpectation(index)} disabled={hasItems}>
          Delete
        </RemoveCargoDetailsButton>
      </div>
    </StyledRow>
  );
};
